/**
 * Translator
 *
 * Central translation registry and DOM translation helper for localized UI text.
 */

import { Roles } from "../constants/roles";
import { dictUI } from "./dictionaries/dict-ui";
import { dictPosts } from "./dictionaries/dict-posts";
import { dictComments } from "./dictionaries/dict-comments";
import { navigationTranslations } from "./dictionaries/dict-navigation";
import { boardsTranslations } from "./dictionaries/dict-boards";
import { productsTranslations } from "./dictionaries/dict-products";
import { brandsTranslations } from "./dictionaries/dict-brands";
import { usersTranslations } from "./dictionaries/dict-users";
import { dictAttachments } from "./dictionaries/dict-attachments";
import { dictModals } from "./dictionaries/dict-modals";
import { dictFormValidation } from "./dictionaries/dict-form-validation";
import { dictMissions } from "./dictionaries/dict-missions";
import { dictNotifications } from "./dictionaries/dict-notifications";
import { FormBase } from "../util/form-base-form";
import { TranslatorListener } from "./translator-listener";

type SupportedLanguage = "th" | "en" | "ja";
type TranslationLeaf = Partial<Record<SupportedLanguage, string>> & Record<string, string | undefined>;
type TranslationNode = TranslationLeaf | { [key: string]: TranslationNode };
type TranslationMap = Record<string, TranslationNode>;
type TranslationDictionaries = Record<string, TranslationMap>;
type TranslationParams = Record<string, string | number>;
type TranslationArg = TranslationParams | string;

class TranslatorFormBase extends FormBase {
    public getLanguageSelect(root: ParentNode = document): HTMLSelectElement | null {
        return this.queryElement(root, `[data-role="${Roles.LANGUAGE_SELECT}"]`, HTMLSelectElement);
    }

    public getLanguageOptions(root: ParentNode = document): HTMLInputElement[] {
        return this.queryElements(root, `[data-role="${Roles.LANGUAGE_SELECT}"]`, HTMLInputElement).filter(
            (input) => input.type === "radio",
        );
    }

    public getNav(root: ParentNode = document): HTMLElement | null {
        return this.queryElement(root, "nav", HTMLElement);
    }

    public getNavLinks(root: ParentNode): HTMLElement | null {
        return this.queryElement(root, ".flex.items-center", HTMLElement);
    }

    public getTranslatableElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, "[data-translate]", HTMLElement);
    }

    public getPlaceholderElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, "[data-translate-placeholder]", HTMLElement);
    }

    public getTitleElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, "[data-translate-title]", HTMLElement);
    }

    public getAriaLabelElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, "[data-translate-aria-label]", HTMLElement);
    }

    public getMultiLanguageElements(root: ParentNode = document): HTMLElement[] {
        return this.queryElements(root, "[data-text-th], [data-text-en], [data-text-ja]", HTMLElement);
    }
}

const translatorFormBase = new TranslatorFormBase();

function isTranslationLeaf(value: TranslationNode | undefined): value is TranslationLeaf {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }

    return (
        ("th" in value && (typeof value.th === "string" || typeof value.th === "undefined")) ||
        ("en" in value && (typeof value.en === "string" || typeof value.en === "undefined")) ||
        ("ja" in value && (typeof value.ja === "string" || typeof value.ja === "undefined"))
    );
}

export class Translator {
    private static readonly PENDING_ATTR = "data-translation-pending";

    public currentLanguage: SupportedLanguage;

    public translations: TranslationMap;

    public dictionaries: TranslationDictionaries;

    public initialized: boolean;

    private warnedMissingKeys: Set<string>;

    public constructor() {
        // Placeholder until init() re-resolves from the DOM (document.documentElement.lang
        // may not be parsed yet at construction time). The language switcher only
        // exists for admin/editor; if it's absent, init() locks to the server-resolved
        // market default (ISS-10-001) instead of this placeholder.
        this.currentLanguage = "th";
        this.translations = {};
        this.dictionaries = {
            navigation: navigationTranslations,
            boards: boardsTranslations,
            products: productsTranslations,
            brands: brandsTranslations,
            users: usersTranslations,
            ui: dictUI,
            comments: dictComments,
            posts: dictPosts,
            attachments: dictAttachments,
            modals: dictModals,
            formValidation: dictFormValidation,
            missions: dictMissions,
            notifications: dictNotifications,
        };
        this.initialized = false;
        this.warnedMissingKeys = new Set();
    }

    private warnMissingTranslationKey(key: string): void {
        if (this.warnedMissingKeys.has(key)) return;

        this.warnedMissingKeys.add(key);
        console.warn("[Translator] Missing translation key:", {
            key,
            language: this.currentLanguage,
        });
    }

    /** localStorage can throw (private mode, quota full) — never let that break init/switch. */
    private readStoredLanguage(): string | null {
        try {
            return localStorage.getItem("language");
        } catch {
            return null;
        }
    }

    private writeStoredLanguage(value: string): void {
        try {
            localStorage.setItem("language", value);
        } catch {
            /* ignore (quota / private mode) */
        }
    }

    /**
     * The language switcher is rendered (navbar settings) only for admin/editor
     * roles — see ISS-01-183. Its presence in the DOM is the signal for whether
     * switching is allowed at all; for everyone else the site is Thai-only.
     */
    public languageSwitchAllowed(): boolean {
        return (
            translatorFormBase.getLanguageOptions(document).length > 0 ||
            translatorFormBase.getLanguageSelect(document) !== null
        );
    }

    public init(): void {
        if (this.initialized) return;
        console.info("[Translator] init:start", {
            htmlLang: document.documentElement.lang,
            pending: document.documentElement.getAttribute(Translator.PENDING_ATTR),
            storedLanguage: this.readStoredLanguage(),
        });
        // General users (no switcher in the DOM) get the server-resolved market
        // default instead of a hardcoded "th" (ISS-10-001) — <html lang> is
        // already set to config('app.locale')/APP_MARKET by ApplyUserLocale.php,
        // so reading it here keeps this in sync without duplicating the env var.
        this.currentLanguage = this.languageSwitchAllowed()
            ? this.normalizeLanguage(this.readStoredLanguage() || this.currentLanguage)
            : this.normalizeLanguage(document.documentElement.lang || "th");
        this.writeStoredLanguage(this.currentLanguage);
        this.loadTranslations();
        this.createTranslator();
        this.bindEvents();
        this.initialized = true;
        document.documentElement.lang = this.currentLanguage;
        this.updateContent();
    }

    public normalizeLanguage(language: string): SupportedLanguage {
        const allowed: SupportedLanguage[] = ["th", "en", "ja"];
        if (allowed.includes(language as SupportedLanguage)) return language as SupportedLanguage;

        console.error("[Translator] Language fallback (invalid value):", {
            value: language,
            allowed,
            inferred: "th",
        });
        return "th";
    }

    public loadTranslations(): void {
        this.translations = {
            ...(this.dictionaries.navigation || {}),
            ...(this.dictionaries.boards || {}),
            ...(this.dictionaries.products || {}),
            ...(this.dictionaries.brands || {}),
            ...(this.dictionaries.users || {}),
            ...(this.dictionaries.ui || {}),
            ...(this.dictionaries.comments || {}),
            ...(this.dictionaries.posts || {}),
            ...(this.dictionaries.attachments || {}),
            ...(this.dictionaries.modals || {}),
            ...(this.dictionaries.formValidation || {}),
            ...(this.dictionaries.missions || {}),
            ...(this.dictionaries.notifications || {}),
        };
    }

    public createTranslator(): void {
        // Only sync the switcher the navbar already rendered (admin/editor).
        // Never inject one where none exists — for guests / general users the
        // site is Thai-only (ISS-01-183).
        const existingOptions = translatorFormBase.getLanguageOptions(document);
        if (existingOptions.length > 0) {
            existingOptions.forEach((option) => {
                option.checked = option.value === this.currentLanguage;
            });
            return;
        }

        const existingSelect = translatorFormBase.getLanguageSelect(document);
        if (existingSelect instanceof HTMLSelectElement) {
            existingSelect.value = this.currentLanguage;
        }
    }

    public bindEvents(): void {
        TranslatorListener.bindContentInserted(() => {
            this.updateUITranslations();
        });

        // Language-switcher bindings only when the switcher exists (admin/editor).
        const languageOptions = translatorFormBase.getLanguageOptions(document);
        if (languageOptions.length > 0) {
            languageOptions.forEach((input) => {
                TranslatorListener.bindLanguageOptionChange(input, (event) => {
                    const target = event.target;
                    if (!(target instanceof HTMLInputElement) || target.type !== "radio" || !target.checked) {
                        return;
                    }
                    this.switchLanguage(this.normalizeLanguage(target.value));
                });
            });
            return;
        }

        const select = translatorFormBase.getLanguageSelect(document);
        if (select) {
            TranslatorListener.bindLanguageSelectChange(select, (event) => {
                const target = event.target;
                if (!(target instanceof HTMLSelectElement)) {
                    return;
                }
                this.switchLanguage(this.normalizeLanguage(target.value));
            });
        }
    }

    public switchLanguage(language: string): void {
        const normalized = this.normalizeLanguage(language);
        console.log("[Translator] switchLanguage:", {
            requested: language,
            normalized,
            previous: this.currentLanguage,
            localStorageBefore: this.readStoredLanguage(),
            documentLangBefore: document.documentElement.lang,
        });
        this.currentLanguage = normalized;
        this.writeStoredLanguage(normalized);
        document.documentElement.lang = normalized;
        translatorFormBase.getLanguageOptions(document).forEach((option) => {
            option.checked = option.value === normalized;
        });

        const select = translatorFormBase.getLanguageSelect(document);
        if (select instanceof HTMLSelectElement) {
            select.value = normalized;
        }

        console.log("[Translator] switchLanguage (applied):", {
            currentLanguage: this.currentLanguage,
            localStorageAfter: this.readStoredLanguage(),
            documentLangAfter: document.documentElement.lang,
        });

        this.updateContent();

        document.dispatchEvent(
            new CustomEvent("languageChanged", {
                detail: { language: normalized },
            }),
        );
    }

    public updateContent(): void {
        this.updateUITranslations();
        this.clearInitialPendingState();
    }

    private clearInitialPendingState(): void {
        console.info("[Translator] pending:clear", {
            htmlLang: document.documentElement.lang,
            currentLanguage: this.currentLanguage,
        });
        document.documentElement.removeAttribute(Translator.PENDING_ATTR);
    }

    public updateUITranslations(): void {
        const buildParamsFromDataAttributes = (element: Element, ignoreAttrs: string[] = []): TranslationParams => {
            const params: TranslationParams = {};
            const ignored = new Set(ignoreAttrs);

            const toCamel = (value: string): string =>
                String(value)
                    .split("-")
                    .filter(Boolean)
                    .map((part, index) => (index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
                    .join("");

            for (const attr of element.getAttributeNames()) {
                if (!attr.startsWith("data-")) continue;
                if (ignored.has(attr)) continue;

                const rawKey = attr.slice("data-".length);
                const attrValue = element.getAttribute(attr);
                if (attrValue === null) continue;
                params[toCamel(rawKey)] = attrValue;
            }

            return params;
        };

        const elements = translatorFormBase.getTranslatableElements(document);
        elements.forEach((element) => {
            const key = element.getAttribute("data-translate");
            const params = buildParamsFromDataAttributes(element, [
                "data-translate",
                "data-translate-placeholder",
                "data-translate-title",
                "data-translate-aria-label",
            ]);

            const translation = this.getTranslation(key, params);
            if (translation) {
                if (element.tagName === "TEXTAREA" || element.tagName === "INPUT") {
                    if (
                        (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
                        !element.value &&
                        !element.getAttribute("data-translate-placeholder")
                    ) {
                        return;
                    }
                } else {
                    element.textContent = translation;
                }
            }
        });

        const placeholders = translatorFormBase.getPlaceholderElements(document);
        placeholders.forEach((element) => {
            const key = element.getAttribute("data-translate-placeholder");
            const translation = this.getTranslation(key);
            if (translation && (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
                element.placeholder = translation;
            }
        });

        const titleElements = translatorFormBase.getTitleElements(document);
        titleElements.forEach((element) => {
            const key = element.getAttribute("data-translate-title");
            const translation = this.getTranslation(key);
            if (translation) {
                element.setAttribute("title", translation);
            }
        });

        const ariaLabelElements = translatorFormBase.getAriaLabelElements(document);
        ariaLabelElements.forEach((element) => {
            const key = element.getAttribute("data-translate-aria-label");
            const params = buildParamsFromDataAttributes(element, [
                "data-translate",
                "data-translate-placeholder",
                "data-translate-title",
                "data-translate-aria-label",
            ]);
            const translation = this.getTranslation(key, params);
            if (translation) {
                element.setAttribute("aria-label", translation);
            }
        });

        const multiLangElements = translatorFormBase.getMultiLanguageElements(document);
        multiLangElements.forEach((element) => {
            const textTh = element.getAttribute("data-text-th");
            const textEn = element.getAttribute("data-text-en");
            const textJa = element.getAttribute("data-text-ja");

            let text = "";
            if (this.currentLanguage === "th" && textTh) {
                text = textTh;
            } else if (this.currentLanguage === "en" && textEn) {
                text = textEn;
            } else if (this.currentLanguage === "ja" && textJa) {
                text = textJa;
            } else {
                text = textTh || textEn || textJa || "";
            }

            if (text) {
                element.textContent = text;
            }
        });
    }

    public getTranslation(key: string | null, params: TranslationArg = {}): string {
        if (!key) return "";
        const translationSet = this.translations[key];
        if (!translationSet || !isTranslationLeaf(translationSet)) {
            this.warnMissingTranslationKey(key);
            return key;
        }

        const translation = translationSet[this.currentLanguage] || translationSet.th || translationSet.en || key;
        const resolvedParams: TranslationParams = typeof params === "string" ? {} : params;

        return translation.replace(/\{\{(\w+)(?::(\w+))?\}\}/g, (match, paramKey: string, formatter?: string) => {
            const value = resolvedParams[paramKey];
            if (value === undefined) return match;

            if (formatter === "toLocaleString" && typeof value === "number") {
                return value.toLocaleString();
            }

            return String(value);
        });
    }

    public getCurrentLanguage(): SupportedLanguage {
        return this.currentLanguage;
    }

    public translate(key: string, params: TranslationArg = {}): string {
        return this.getTranslation(key, params);
    }
}

const translator = new Translator();
export { translator };
